import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Box, CircularProgress, Typography } from '@mui/material';
import { TeamChatChannel } from 'app/shared/components/TeamChat';
import PropTypes from 'prop-types';

import { logger } from 'utils/logger';

import { initializeChannelAction } from 'store/teamChat/actions';

import {
  bodyStyles,
  errorStyles,
  getChannelPanelStyles,
  loadingStyles,
} from '../../LoanEditorChatDrawer.styles';
import { getChatInitializationErrorMessage } from '../../LoanEditorChatDrawer.utils';

const LoanEditorChatChannelPanel = memo(
  ({
    expectedChatId,
    chatName,
    currentUserId,
    companyId,
    subCompanyId,
    isSubCompanyUser,
    knownMemberNamesByUserId = {},
    priorityMemberIds,
    isActive,
    showEntityReferencesRail = false,
  }) => {
    const dispatch = useDispatch();

    const [chatId, setChatId] = useState(null);
    const [isInitializing, setIsInitializing] = useState(false);
    const [initializationError, setInitializationError] = useState(null);

    const initializedForRef = useRef(null);

    const onInitializeChat = useCallback(async () => {
      if (!expectedChatId || initializedForRef.current === expectedChatId || isInitializing) {
        return;
      }

      initializedForRef.current = expectedChatId;
      setIsInitializing(true);
      setInitializationError(null);

      try {
        const connectionChatId = await dispatch(
          initializeChannelAction({
            expectedChatId,
            chatName,
            currentUserId,
            companyId,
            subCompanyId,
            isSubCompanyUser,
            knownMemberNamesByUserId,
          }),
        );

        setChatId(connectionChatId);
      } catch (error) {
        // Keep initializedForRef pinned to this chat id on failure. Resetting it to null re-opened
        // initialization on the next render, and because `isInitializing` is a dependency of this
        // callback, toggling it churns the callback identity and re-fires the effect — turning a
        // single failed init (e.g. a subtree-scoped TPO broker who 403s reading the chat on the
        // Notifications service) into an unbounded getChatInfo retry loop. Attempt once, then
        // surface the error and stop (DEV-4226).
        logger.error('[LoanEditorChatChannelPanel] Failed to initialize chat', error);
        setInitializationError(error);
      } finally {
        setIsInitializing(false);
      }
    }, [
      expectedChatId,
      isInitializing,
      chatName,
      currentUserId,
      companyId,
      subCompanyId,
      isSubCompanyUser,
      knownMemberNamesByUserId,
      dispatch,
    ]);

    useEffect(() => {
      if (!isActive) {
        return;
      }

      onInitializeChat();
    }, [isActive, onInitializeChat]);

    return (
      <Box sx={getChannelPanelStyles(isActive)}>
        <Box sx={bodyStyles}>
          {isInitializing && !chatId && (
            <Box sx={loadingStyles}>
              <CircularProgress size={24} />
            </Box>
          )}
          {!!initializationError && !chatId && (
            <Box sx={errorStyles}>
              <Typography variant="regular14">
                {getChatInitializationErrorMessage(initializationError)}
              </Typography>
            </Box>
          )}
          {!!chatId && (
            <TeamChatChannel
              canShareCurrentView
              isHeaderHidden
              chatId={chatId}
              currentUserId={currentUserId}
              fallbackTitle={chatName}
              isActive={isActive}
              key={chatId}
              priorityMemberIds={priorityMemberIds}
              showEntityReferencesRail={showEntityReferencesRail}
            />
          )}
        </Box>
      </Box>
    );
  },
);
LoanEditorChatChannelPanel.propTypes = {
  expectedChatId: PropTypes.string.isRequired,
  chatName: PropTypes.string,
  currentUserId: PropTypes.string,
  companyId: PropTypes.string,
  subCompanyId: PropTypes.string,
  isSubCompanyUser: PropTypes.bool,
  knownMemberNamesByUserId: PropTypes.objectOf(PropTypes.string),
  priorityMemberIds: PropTypes.arrayOf(PropTypes.string),
  isActive: PropTypes.bool,
  showEntityReferencesRail: PropTypes.bool,
};

export default LoanEditorChatChannelPanel;
